import type {
  CatalogChart,
  ChartData,
  DataSource,
  GeneratedChart,
  SavedChart,
  SavedDashboard,
  SavedWidget,
} from '../types';

/**
 * The session token lives in memory only. It arrives in the URL fragment from
 * the platform handoff and is stripped immediately — keeping it out of
 * localStorage means a stored token can't be replayed from another tab or by
 * anything that can read storage.
 */
let sessionToken: string | null = null;

export const setSessionToken = (token: string | null): void => {
  sessionToken = token;
};

export const hasSession = (): boolean => sessionToken !== null;

/** Read `#rt=<token>` from the handoff redirect, then scrub it from the URL. */
export const captureSessionFromFragment = (): void => {
  const hash = window.location.hash;
  if (!hash.startsWith('#')) return;

  const params = new URLSearchParams(hash.slice(1));
  const token = params.get('rt');
  if (!token) return;

  setSessionToken(token);
  params.delete('rt');
  const rest = params.toString();
  window.history.replaceState(
    null,
    '',
    `${window.location.pathname}${window.location.search}${rest ? `#${rest}` : ''}`,
  );
};

class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const request = async <T>(path: string, init: RequestInit = {}): Promise<T> => {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...((init.headers as Record<string, string>) ?? {}),
  };
  if (init.body) headers['Content-Type'] = 'application/json';
  if (sessionToken) headers.Authorization = `Bearer ${sessionToken}`;

  const res = await fetch(path, { ...init, headers });

  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) detail = body.error;
    } catch {
      /* non-JSON error body — keep the status text */
    }
    throw new ApiError(detail, res.status);
  }

  return (await res.json()) as T;
};

const post = <T>(path: string, body: unknown): Promise<T> =>
  request<T>(path, { method: 'POST', body: JSON.stringify(body) });

export interface QueryInput {
  source: string;
  chartId: string;
  params?: Record<string, string>;
}

export const api = {
  sources: (): Promise<DataSource[]> => request('/api/sources'),

  catalog: (sourceId: string): Promise<CatalogChart[]> =>
    request(`/api/sources/${encodeURIComponent(sourceId)}/charts`),

  /** The core call: the platform's query goes in, chart data comes back. */
  query: ({ source, chartId, params = {} }: QueryInput): Promise<ChartData> =>
    post('/api/query', { source, chartId, params }),

  generateChart: (input: QueryInput & { prompt: string; previousHtml?: string }): Promise<GeneratedChart> =>
    post('/api/ai/chart', {
      source: input.source,
      chartId: input.chartId,
      params: input.params ?? {},
      prompt: input.prompt,
      previousHtml: input.previousHtml,
    }),

  charts: (): Promise<SavedChart[]> => request('/api/charts'),
  saveChart: (chart: Partial<SavedChart>): Promise<SavedChart> => post('/api/charts', chart),
  deleteChart: (id: string): Promise<{ ok: true }> =>
    request(`/api/charts/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  dashboards: (): Promise<SavedDashboard[]> => request('/api/dashboards'),
  dashboard: (idOrSlug: string): Promise<SavedDashboard> =>
    request(`/api/dashboards/${encodeURIComponent(idOrSlug)}`),
  saveDashboard: (dashboard: Partial<SavedDashboard>): Promise<SavedDashboard> =>
    post('/api/dashboards', dashboard),
  deleteDashboard: (id: string): Promise<{ ok: true }> =>
    request(`/api/dashboards/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  widgets: (): Promise<SavedWidget[]> => request('/api/widgets'),
  saveWidget: (widget: Partial<SavedWidget>): Promise<SavedWidget> => post('/api/widgets', widget),
  deleteWidget: (id: string): Promise<{ ok: true }> =>
    request(`/api/widgets/${encodeURIComponent(id)}`, { method: 'DELETE' }),
};

export { ApiError };
